import { createFileRoute } from "@tanstack/react-router";
import DealerPublicPage from "@/pages/DealerPublicPage";
import { routeHead } from "@/lib/seo/route-head";
import { supabase } from "@/integrations/supabase/client";
import { dealerHead } from '@/lib/seo/dealer-seo';

type DealerSeo = {
  id: string;
  company_name: string | null;
  showroom_name: string | null;
  city: string | null;
  company_description: string | null;
  logo_url: string | null;
  business_phone: string | null;
  business_email: string | null;
  business_address: string | null;
  website_url: string | null;
  average_rating: number | null;
  total_reviews: number | null;
};

export const Route = createFileRoute("/dealeri/$alias/")({
  loader: async ({ params }) => {
    const { data } = await supabase.rpc("get_dealer_by_alias", { _alias: params.alias });
    const row = Array.isArray(data) ? (data[0] as (DealerSeo & Record<string, unknown>) | undefined) : undefined;
    if (!row) return { alias: params.alias, dealer: null, listings: [] };
    const { data: verification } = await supabase.from('dealer_profiles').select('verified, risco_verified, business_name').eq('id', row.id).maybeSingle();

    const { data: listingRows } = await supabase.rpc("dealer_page_listings", {
      _alias: params.alias,
      _limit: 120,
      _offset: 0,
    });

    return {
      alias: params.alias,
      dealer: { ...row, company_name: row.company_name || row.showroom_name || verification?.business_name || null, verified: verification?.verified === true || verification?.risco_verified === true },
      listings: Array.isArray(listingRows) ? listingRows : [],
    };
  },
  head: ({ params, loaderData }) => {
    const dealer = loaderData?.dealer;
    const base = dealerHead(`/dealeri/${params.alias}`, dealer?.company_name || dealer?.showroom_name || null, dealer?.city ?? null);
    if (!dealer) return base;


    const url = `https://roautomarket.ro/dealeri/${params.alias}`;
    const name = dealer.company_name || dealer.showroom_name || params.alias;

    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "AutoDealer",
      name,
      url,
      ...(dealer.company_description ? { description: dealer.company_description } : {}),
      ...(dealer.logo_url ? { image: dealer.logo_url, logo: dealer.logo_url } : {}),
      ...(dealer.business_phone ? { telephone: dealer.business_phone } : {}),
      ...(dealer.business_email ? { email: dealer.business_email } : {}),
      ...(dealer.website_url ? { sameAs: [dealer.website_url] } : {}),
      ...(dealer.business_address || dealer.city
        ? {
            address: {
              "@type": "PostalAddress",
              ...(dealer.business_address ? { streetAddress: dealer.business_address } : {}),
              ...(dealer.city ? { addressLocality: dealer.city } : {}),
              addressCountry: "RO",
            },
          }
        : {}),
      ...(dealer.average_rating && dealer.total_reviews
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: dealer.average_rating,
              reviewCount: dealer.total_reviews,
            },
          }
        : {}),
    };

    return {
      ...base,
      scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <DealerPublicPage />
  );
}
